import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAppHeader } from '../contexts/AppHeaderContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LogOut, Moon, Sun, Globe, Settings, User, ArrowLeft, RefreshCw, CreditCard, Link2, Plug,
} from 'lucide-react';
import ZitiusLogo from './brand/ZitiusLogo';
import PlanUpgradeModal from './PlanUpgradeModal';
import ScreenshotButton from './ScreenshotButton';
import api from '../utils/axiosConfig';

const AppHeader = () => {
  const { user, logout } = useAuth();
  const [userPlans, setUserPlans] = useState({});
  const [planModalOpen, setPlanModalOpen] = useState(false);

  const fetchPlans = () => {
    api.get('/api/plans/me')
      .then((res) => setUserPlans(res.data || {}))
      .catch(() => setUserPlans({}));
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    const handler = () => fetchPlans();
    window.addEventListener('planSubscriptionUpdated', handler);
    return () => window.removeEventListener('planSubscriptionUpdated', handler);
  }, []);

  const firstPlan = user?.roles?.[0] ? userPlans[user.roles[0]] : null;
  const planLabel = firstPlan?.name || null;
  const { t, language, setLanguage } = useLanguage();
  const { mode, toggleMode } = useTheme();
  const { centerContent, rightContent, showBackButton } = useAppHeader();
  const navigate = useNavigate();

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <header className='citius-screenshot-header-chrome sticky top-0 z-40 border-b border-border bg-card/85 backdrop-blur-xl supports-[backdrop-filter]:bg-card/55'>
      <div className='flex h-16 items-center px-6'>
        <div className='flex items-center gap-3'>
          {showBackButton ? (
            <Button variant='ghost' size='sm' onClick={() => navigate('/dashboard')}>
              <ArrowLeft className='h-4 w-4 mr-2' />
              {t('common.back')}
            </Button>
          ) : (
            <Link to='/dashboard' className='hover:opacity-80 transition-opacity'>
              <ZitiusLogo size="sm" />
            </Link>
          )}
        </div>

        {centerContent && (
          <div className='ml-3 min-[480px]:ml-6 flex-1 flex items-center min-w-0 overflow-hidden'>
            {centerContent}
          </div>
        )}

        <div className='ml-auto flex items-center gap-2'>
          {user?.roles?.includes('athlete') && (
            <Button variant='ghost' size='sm' asChild className='hidden sm:inline-flex'>
              <Link to='/coaches'>{t('nav.coaches')}</Link>
            </Button>
          )}
          {!!user && <ScreenshotButton />}
          {user?.roles?.includes('athlete') && (
            <Button
              variant='ghost'
              size='icon'
              onClick={() => window.dispatchEvent(new CustomEvent('openStravaConnectDocModal'))}
              title={t('strava.docPreviewButtonTitle')}
              className='text-[#FC4C02] hover:text-[#E34402] hover:bg-[#FC4C02]/10'
            >
              <Link2 className='h-4 w-4' />
            </Button>
          )}
          <Button
            variant='ghost'
            size='icon'
            onClick={() => window.dispatchEvent(new CustomEvent('openSyncModal'))}
            title={t('strava.syncActivities')}
          >
            <RefreshCw className='h-4 w-4' />
          </Button>

          <Button
            variant='ghost'
            size='icon'
            onClick={() => setLanguage(language === 'en' ? 'no' : 'en')}
            title={t('nav.language')}
          >
            <Globe className='h-4 w-4' />
          </Button>

          <Button
            variant='ghost'
            size='icon'
            onClick={toggleMode}
            title={mode === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
          >
            {mode === 'dark' ? <Sun className='h-4 w-4' /> : <Moon className='h-4 w-4' />}
          </Button>

          {rightContent}

          <PlanUpgradeModal open={planModalOpen} onOpenChange={setPlanModalOpen} />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' className='relative h-8 w-8 rounded-full'>
                <Avatar className='h-8 w-8'>
                  <AvatarFallback className='citius-screenshot-avatar-initials bg-primary text-primary-foreground text-xs'>
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='w-56' align='end' forceMount>
              <DropdownMenuLabel className='font-normal'>
                <div className='flex flex-col space-y-1'>
                  <p className='text-sm font-medium leading-none'>{user?.name}</p>
                  <p className='text-xs leading-none text-muted-foreground'>{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => window.dispatchEvent(new CustomEvent('openProfileModal'))}
              >
                <User className='mr-2 h-4 w-4' />
                {t('nav.profile')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => window.dispatchEvent(new CustomEvent('openServicesModal'))}
              >
                <Plug className='mr-2 h-4 w-4' />
                {t('nav.services')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.dispatchEvent(new CustomEvent('openSettings'))}>
                <Settings className='mr-2 h-4 w-4' />
                {t('nav.settings')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPlanModalOpen(true)}>
                <CreditCard className='mr-2 h-4 w-4 shrink-0' />
                <span className='truncate'>{planLabel || t('plans.myPlan')}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className='text-destructive'>
                <LogOut className='mr-2 h-4 w-4' />
                {t('auth.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
